import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FlightClass } from '@websky/graphql';
import { Tabs } from '../../types';
import { getSelectedFares } from '../../../Results/store/selectedFares/selectors';
import { getMinPrice } from '../../../utils';
import { Money } from '../../../Money';
import { getSelectedExchangeVariants } from '@entities/exchange';
export const useLastSelectedFlight = () => {
    var _a, _b;
    try {
        const lastSelectedResultFlights = (_a = useSelector(getSelectedFares)) !== null && _a !== void 0 ? _a : [];
        if (lastSelectedResultFlights.length) {
            return lastSelectedResultFlights[lastSelectedResultFlights.length - 1];
        }
        const lastSelectedExchangeFlights = (_b = useSelector(getSelectedExchangeVariants)) !== null && _b !== void 0 ? _b : [];
        if (lastSelectedExchangeFlights.length) {
            return lastSelectedExchangeFlights[lastSelectedExchangeFlights.length - 1];
        }
        return null;
    }
    catch (_c) {
        return null;
    }
};
export const useDesktopFlightModalContent = (props) => {
    const [active, onSelect] = useState(() => {
        var _a, _b, _c, _d, _e;
        const hasBusinessPrices = (_a = props.businessFares) === null || _a === void 0 ? void 0 : _a.some(({ prices }) => prices), hasEconomyPrices = (_b = props.economyFares) === null || _b === void 0 ? void 0 : _b.some(({ prices }) => prices), hasMilesPrices = (_c = props.milesFares) === null || _c === void 0 ? void 0 : _c.some(({ prices }) => prices);
        const availableServices = [];
        if (hasEconomyPrices) {
            availableServices.push({
                flightClass: FlightClass.Economy,
                tab: Tabs.EconomyFares
            });
        }
        if (hasBusinessPrices) {
            availableServices.push({
                flightClass: FlightClass.Business,
                tab: Tabs.BusinessFares
            });
        }
        if (hasMilesPrices) {
            availableServices.push({
                flightClass: FlightClass.Miles,
                tab: Tabs.MilesFares
            });
        }
        if (props.preselectedFlightClass) {
            const existService = availableServices.find(service => service.flightClass === props.preselectedFlightClass);
            if (existService) {
                return existService.tab;
            }
        }
        const lastSelectedFareCategory = (_d = props.lastSelectedFare) === null || _d === void 0 ? void 0 : _d.category;
        if (!!lastSelectedFareCategory) {
            const existsService = availableServices.find(service => service.flightClass === lastSelectedFareCategory);
            if (existsService) {
                return existsService.tab;
            }
        }
        return (_e = availableServices[0]) === null || _e === void 0 ? void 0 : _e.tab;
    });
    const [isFareCompareOpen, setFareCompareOpen] = useState(false);
    const openFareCompare = useCallback(() => {
        setFareCompareOpen(true);
    }, [isFareCompareOpen]);
    const closeFareCompare = useCallback(() => {
        setFareCompareOpen(false);
    }, [isFareCompareOpen]);
    const handleEconomyButtonCLick = useCallback(() => {
        onSelect(Tabs.EconomyFares);
    }, [active]);
    const handleBusinessButtonClick = useCallback(() => {
        onSelect(Tabs.BusinessFares);
    }, [active]);
    const handleMilesButtonClick = useCallback(() => {
        onSelect(Tabs.MilesFares);
    }, [active]);
    const handleFlightDetailsButtonClick = useCallback(() => {
        onSelect(Tabs.FlightInfo);
    }, [active]);
    return {
        active,
        handleEconomyButtonCLick,
        handleBusinessButtonClick,
        handleMilesButtonClick,
        handleFlightDetailsButtonClick,
        isFareCompareOpen,
        openFareCompare,
        closeFareCompare
    };
};
export const useDesktopFlightModalMobileTabs = ({ isEconomyExists, isMilesExists, isBusinessFaresExists, isActiveEconomyFares, isActiveBusinessFares, isActiveMilesFares, handleEconomyButtonCLick, handleBusinessButtonClick, handleMilesButtonClick, economyFares, businessFares, milesFares }) => {
    const { t } = useTranslation('FlightInfoModalContent');
    return useMemo(() => {
        const faresCategory = [];
        const getMoneyElement = (fares) => {
            try {
                // Sabre (DV) exchange has no prices when search exchange variants
                // WEBSKY-7642
                const minPrices = getMinPrice(fares.reduce((prices, fare) => { var _a; return [...prices, ...((_a = fare.prices) !== null && _a !== void 0 ? _a : [])]; }, []), price => price.price);
                return (React.createElement(React.Fragment, null,
                    t('Checkout:from'),
                    " ",
                    React.createElement(Money, { money: minPrices })));
            }
            catch (e) {
                return React.createElement(React.Fragment, null, "\u2013");
            }
        };
        if (isEconomyExists || (!isMilesExists && !isBusinessFaresExists)) {
            faresCategory.push({
                label: t('Economy'),
                description: getMoneyElement(economyFares),
                active: isActiveEconomyFares,
                onClick: handleEconomyButtonCLick
            });
        }
        if (isBusinessFaresExists) {
            faresCategory.push({
                label: t('Business'),
                description: getMoneyElement(businessFares),
                active: isActiveBusinessFares,
                onClick: handleBusinessButtonClick
            });
        }
        if (isMilesExists) {
            faresCategory.push({
                label: t('Premium'),
                description: getMoneyElement(milesFares),
                active: isActiveMilesFares,
                onClick: handleMilesButtonClick
            });
        }
        return {
            tabs: faresCategory.map(fare => ({
                label: fare.label,
                description: fare.description
            })),
            active: faresCategory.findIndex(fare => fare.active),
            onChange: index => faresCategory[index].onClick()
        };
    }, [
        t,
        isEconomyExists,
        isMilesExists,
        isBusinessFaresExists,
        isActiveEconomyFares,
        isActiveBusinessFares,
        isActiveMilesFares,
        handleEconomyButtonCLick,
        handleBusinessButtonClick,
        handleMilesButtonClick,
        economyFares,
        businessFares,
        milesFares
    ]);
};
